import { SnapshotModel, getModelFromRef } from '../index';
import { Collections } from '../../constants';
import { Customer, ESnapshotExists } from '../../types';
import { getOverrideAAC } from '../../types/affidavits/convertARS';
import { getErrorReporter } from '../../utils/errors';
import { AffidavitReconciliationSettings } from '../../types/organization';
import { removeUndefinedFields } from '../../helpers';
import { OrganizationModel } from './organizationModel';
import { ColumnService } from '../../services/directory';
import { ResponseOrError, wrapError, wrapSuccess } from '../../types/responses';
import { UserModel } from './userModel';
import { safeGetModelFromRef } from '../getModel';
import { isOrderRate, OrderRate } from '../../types/rates';
import {
  BadRequestError,
  ColumnError,
  NotFoundError,
  wrapErrorAsColumnError
} from '../../errors/ColumnErrors';
import { safeGetOrThrow } from '../../safeWrappers';

export class CustomerModel extends SnapshotModel<
  Customer,
  typeof Collections.customers
> {
  type = Collections.customers;

  private publisher: OrganizationModel | null = null;

  /**
   * Updates the account number on the customer.
   *
   * @param {string} accountNumber - The new account number to update.
   * @returns {Promise<string>} - A promise that resolves to the account number
   */
  async maybeUpdateAccountNumberOnCustomer(
    accountNumber: string
  ): Promise<string> {
    if (this.modelData.internalID) return this.modelData.internalID;
    await this.update({
      internalID: accountNumber
    });
    return accountNumber;
  }

  public async getPublisher(): Promise<OrganizationModel> {
    if (!this.publisher) {
      this.publisher = await getModelFromRef(
        OrganizationModel,
        this.ctx,
        this.modelData.organization
      );
    }

    return this.publisher;
  }

  public async updateAutomatedAffidavitConfiguration(
    affidavitReconciliationSettings: Partial<AffidavitReconciliationSettings>
  ): Promise<void> {
    const publisher = await this.getPublisher();
    const { response: automatedAffidavitConfiguration, error } =
      await getOverrideAAC(
        this.ctx,
        publisher,
        affidavitReconciliationSettings
      );

    if (error) {
      getErrorReporter().logAndCaptureError(
        ColumnService.AFFIDAVITS,
        error,
        'Failed to get automated affidavit configuration from affidavit reconciliation settings for customer',
        {
          customerId: this.id
        }
      );

      return await this.ref.update({
        affidavitReconciliationSettings
      });
    }

    await this.ref.update(
      removeUndefinedFields({
        affidavitReconciliationSettings,
        automatedAffidavitConfiguration
      })
    );
  }

  public async getUser(): Promise<ResponseOrError<UserModel>> {
    return safeGetModelFromRef(UserModel, this.ctx, this.modelData.user);
  }

  public async getEmail(): Promise<ResponseOrError<string>> {
    const userModelResult = await this.getUser();

    if (userModelResult.error) {
      return userModelResult;
    }

    return wrapSuccess(userModelResult.response.modelData.email);
  }

  public async getDefaultFilingTypeRate(
    filingTypeId: string,
    { isDisplayLayout } = { isDisplayLayout: false }
  ): Promise<ResponseOrError<ESnapshotExists<OrderRate> | null, ColumnError>> {
    const customerFilingTypeRates = this.modelData.filingTypeRates || {};
    const customerFilingTypeRateConfig =
      customerFilingTypeRates[filingTypeId] || {};

    const rateRef = isDisplayLayout
      ? customerFilingTypeRateConfig.displayRate
      : customerFilingTypeRateConfig.linerRate;

    if (!rateRef) {
      return wrapSuccess(null);
    }

    const { response: rateSnap, error: rateError } = await safeGetOrThrow(
      rateRef
    );
    if (rateError) {
      return wrapErrorAsColumnError(rateError, NotFoundError);
    }

    const rate = rateSnap.data();

    if (!isOrderRate(rate)) {
      return wrapError(
        new BadRequestError('Received rate inapplicable to order')
      );
    }

    return wrapSuccess(rateSnap as ESnapshotExists<OrderRate>);
  }
}
